import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { BackdropLoader, DatePicker, ExpandedTooltip, Icon, toast } from '@livable/saas-react-components';
import { Button, Chip, FormControlLabel, FormGroup, IconButton, Switch, Typography } from '@mui/material';
import { formatISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import FSEBillModal from 'src/components/resident/ResidentModal/MoveOutTab/FSEBillModal';
import { useStore } from 'src/stores';
import { dateToFormat } from 'src/utils/date';
import { useBatch } from 'src/utils/hooks';
import { getSentFinalStatementWarning } from './sentFinalStatementWarning';
import useMoveOutStyles from './styles';
import useStyles from '../styles';

// ? i18n
export const MoveOutTab = observer(_ref => {
  var _store$fse$finalState, _store$fse$fsePosted;
  let accountId = _ref.accountId,
    propertyId = _ref.propertyId,
    residencyId = _ref.residencyId,
    unitName = _ref.unitName,
    parentToggleRefreshCallback = _ref.parentToggleRefreshCallback;
  const store = useStore();
  const classes = useStyles();
  const moveOutClasses = useMoveOutStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState('initial'),
    _useState2 = _slicedToArray(_useState, 2),
    isBatched = _useState2[0],
    setIsBatched = _useState2[1];
  const _useState3 = useState('initial'),
    _useState4 = _slicedToArray(_useState3, 2),
    isSubmitBillBatched = _useState4[0],
    setIsSubmitBillBatched = _useState4[1];
  const finalStatementInfo = (_store$fse$finalState = store.fse.finalStatementInfo.filter(f => f.residencyId === residencyId)) === null || _store$fse$finalState === void 0 ? void 0 : _store$fse$finalState[0];
  const fseBatchId = store.residencies.fseBatchId;
  const isFinalStatementEstimationEnabled = store.account.featureFlagsStore.isFinalStatementEstimationEnabled;
  const postFseAutomatically = store.residencies.postFseAutomatically;
  const residentName = store.account.profilesFullName.get(accountId);
  const submitBillBatchId = (_store$fse$fsePosted = store.fse.fsePosted) === null || _store$fse$fsePosted === void 0 ? void 0 : _store$fse$fsePosted.batchId;
  const days = finalStatementInfo === null || finalStatementInfo === void 0 ? void 0 : finalStatementInfo.overdueDays;
  const status = finalStatementInfo === null || finalStatementInfo === void 0 ? void 0 : finalStatementInfo.status;
  const isInactive = status === 'Inactive';
  const chosenDate = store.residencies.datePickermoveOutDate;
  const setChosenDate = store.residencies.setDatePickermoveOutDate;
  const disabled = !chosenDate || isBatched === 'start' || store.residencies.updateResidencyMoveOutDateAsync.inProgress;
  const isToggleDisabled = status === 'SentToResident';
  const sentFinalStatementWarning = getSentFinalStatementWarning(finalStatementInfo, store.company.companyFeatureStore.isAppfolio);
  const getFinalStatements = async () => {
    const resp = await store.fse.getFinalStatementsAsync.run({
      residencyId
    });
    return resp;
  };
  const getFSEInfos = async () => {
    const resp = await store.fse.getFSEInfosAsync.run({
      residencyIds: [residencyId]
    });
    return resp;
  };
  const getUserProfile = async data => {
    const resp = await store.account.getUsersProfilesAsync.run(data);
    return resp;
  };
  const getUtilityInfo = async ids => {
    if (!store.account.featureFlagsStore.getIsBillAutomationEnabled || !store.account.featureFlagsStore.getIsBillingTemplatesEnabled) {
      const resp = await store.utilityProviders.getUtilityInfoAsync.run({
        utilityIds: ids
      });
      return resp;
    } else {
      const resp = await store.utilityProviders.getPropertyUtilityInfoAsync.run({
        utilityIds: ids,
        propertyId
      });
      return resp;
    }
  };
  const fetchResidencyMoveOutDate = async () => {
    await store.residencies.getResidencyMoveOutDateAsync.run({
      residencyId
    });
    await store.residencies.getResidencyBillingSettingsAsync.run({
      residencyId
    });
  };
  const submitFinalStatements = async () => {
    const resp = await store.fse.submitFinalStatementsAsync.run({
      residencyId
    });
    return resp;
  };
  const getStatemens = () => getFinalStatements().then(() => getUtilityInfo(store.fse.finalStatements.map(item => item.utilityId)));
  useBatch(fseBatchId, () => store.fse.getFSEInfosAsync.run({
    residencyIds: [residencyId]
  }), () => setIsBatched('start'), () => setIsBatched('end'), 'There was an error while getting move-out date. Please try again.');
  useBatch(submitBillBatchId, () => store.fse.getFSEInfosAsync.run({
    residencyIds: [residencyId]
  }), () => setIsSubmitBillBatched('start'), () => setIsSubmitBillBatched('end'), 'There was an error while getting move-out info. Please try again.');
  useEffect(() => {
    Promise.all([getFSEInfos(), getUserProfile({
      accountIds: [accountId]
    }), fetchResidencyMoveOutDate()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isBatched === 'end') setIsBatched('batched');
  }, [isBatched]);
  useEffect(() => {
    if (isSubmitBillBatched === 'end') setIsSubmitBillBatched('batched');
  }, [isSubmitBillBatched]);
  const getCriticalChip = label => /*#__PURE__*/React.createElement(Chip, {
    label: label,
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "warning"
    }),
    size: "large",
    variant: "critical"
  });
  const getDaysText = () => "day".concat(days > 1 ? 's' : '');
  const getText = () => {
    if (status === 'Inactive') {
      return "The final statement will be generated on ".concat(dateToFormat(finalStatementInfo.avaliableFrom), ", 30 days before move-out. We will notify you when it\u2019s ready.");
    } else if (status === 'ReadyForReview') {
      if (store.company.companyFeatureStore.isAppfolio) {
        return 'The final statement was generated please review the batch and it will be send to the resident';
      } else {
        return "The final statement was generated please review it and send to the resident by the move-out date  ".concat(dateToFormat(finalStatementInfo.avaliableFrom), ".");
      }
    } else if (status === 'SentToResident' && postFseAutomatically) {
      var _finalStatementInfo$s;
      return "The final statement was generated and automatically sent to the resident on ".concat(dateToFormat((_finalStatementInfo$s = finalStatementInfo.statement) === null || _finalStatementInfo$s === void 0 ? void 0 : _finalStatementInfo$s.timestamp), ".");
    } else if (status === 'SentToResident' && !postFseAutomatically) {
      var _finalStatementInfo$s2;
      return "The final statement was sent to the resident on ".concat(dateToFormat((_finalStatementInfo$s2 = finalStatementInfo.statement) === null || _finalStatementInfo$s2 === void 0 ? void 0 : _finalStatementInfo$s2.timestamp), ".");
    } else if (store.fse.FSEBillModalToggle && days > 15) {
      return "The final statement will be generated and automatically sent to the resident on ".concat(dateToFormat(finalStatementInfo.avaliableFrom), ", 30 days before move-out. If you want to review and make adjustments to the final statement, turn off the automatic send above.");
    } else if (status === 'Inactive' || !store.fse.FSEBillModalToggle && days > 15) {
      return "Final statement will become active on ".concat(dateToFormat(finalStatementInfo.avaliableFrom), ", 30 days before move-out. We will notify you when it's ready.");
    }
    if (days > 0) {
      return /*#__PURE__*/React.createElement("div", {
        className: classes.smSection
      }, getCriticalChip("The resident move-out date was ".concat(days, " ").concat(getDaysText(), " ago, please review final statement and send it as soon as\n              possible")));
    }
  };

  // const isSubmitDisabled = () => {
  //   if (postFseAutomatically !== store.fse.FSEBillModalToggle) return;
  //   if (!chosenDate) return true;
  //   else if (chosenDate && moveOutDateOnly === formatISO(chosenDate).split('T')[0])
  //     return disabled || finalStatementInfo;
  // };

  const getChipProps = () => {
    switch (status) {
      case 'Inactive':
        return {
          text: 'Inactive',
          variant: 'inactive'
        };
      case 'Overdue':
        return {
          text: 'Overdue',
          variant: 'warning'
        };
      case 'ProcessingError':
        return {
          text: 'Error',
          variant: 'critical'
        };
      case 'ReadyForReview':
        return {
          text: 'Ready',
          variant: 'success'
        };
      case 'SentToResident':
        return {
          text: 'Sent',
          variant: 'inactive'
        };
    }
  };
  const handleCancel = async () => {
    const data = {
      message: '',
      moveOutDateOnly: null,
      postFinalStatement: 'None',
      residencyId
    };
    const resp = await store.residencies.updateResidencyMoveOutDateAsync.run(data);
    if (resp && resp.isSuccessful) {
      toast('success', {
        body: ['Successfully canceled']
      });
      setChosenDate(null);
      store.fse.clearFinalStatementInfo();
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      parentToggleRefreshCallback && parentToggleRefreshCallback();
    }
  };
  const handleSubmit = async () => {
    const data = {
      moveOutDateOnly: formatISO(chosenDate).split('T')[0],
      postFseAutomatically: store.fse.FSEBillModalToggle,
      residencyId
    };
    const resp = await store.residencies.updateResidencyMoveOutDateAsync.run(data);
    if (resp && resp.isSuccessful) {
      toast('success', {
        body: ['Successfully saved']
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      parentToggleRefreshCallback && parentToggleRefreshCallback();
    }
  };
  const handleSubmitFinalStatements = () => submitFinalStatements().then(() => {
    toast('success', {
      body: ['Final statement sent']
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    parentToggleRefreshCallback && parentToggleRefreshCallback();
  }).then(() => getStatemens());
  const handleToggle = pros => store.fse.setFSEBillModalToggle(pros.target.checked);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.tabWithFooter
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.content
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, store.company.companyFeatureStore.isAppfolio ? 'Move out date' : 'Move-out schedule', /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: t('move_out_modal.schedule_move-out_tooltip'),
    type: "grey"
  }, /*#__PURE__*/React.createElement(IconButton, {
    className: classes.inlineTooltip
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "info"
  }))), isFinalStatementEstimationEnabled && !store.company.companyFeatureStore.isAppfolio && /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, "Please schedule the move-out date so we could estimate the charges and generate the final statement.")), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.datePicker, " ").concat(classes.mdSection, " ").concat(store.company.companyFeatureStore.readOnlyScheduleMoveOut ? 'disabled' : '')
  }, /*#__PURE__*/React.createElement(DatePicker, {
    format: "MM/dd/yy",
    label: t('unit_modal.move-out_date'),
    onChange: date => setChosenDate(date),
    value: chosenDate
  })), sentFinalStatementWarning ? /*#__PURE__*/React.createElement("div", {
    className: classes.mdSection
  }, /*#__PURE__*/React.createElement(Chip, {
    label: sentFinalStatementWarning,
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "warning"
    }),
    size: "large",
    variant: "warning"
  })) : null, isFinalStatementEstimationEnabled && !store.company.companyFeatureStore.readOnlyScheduleMoveOut && /*#__PURE__*/React.createElement("div", {
    className: "".concat(isToggleDisabled ? classes.disabled : '', " ").concat(classes.sendAutomatically)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.sendBillToggle
  }, /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(FormControlLabel, {
    checked: store.fse.FSEBillModalToggle,
    control: /*#__PURE__*/React.createElement(Switch, {
      disabled: isToggleDisabled
    }),
    label: /*#__PURE__*/React.createElement(Typography, {
      variant: "body3"
    }, t('fse_page.automatically_move_out_title')),
    onClick: handleToggle
  })), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: 'Unchecking this means you are opting in to review this final statement manually and we will not send it unless you review and approve it.',
    type: "grey"
  }, /*#__PURE__*/React.createElement(IconButton, {
    className: classes.inlineTooltip
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "info"
  })))), /*#__PURE__*/React.createElement(Typography, {
    className: classes.sendBillText,
    variant: "body1"
  }, t('fse_page.automatically_move_out_text'))), isFinalStatementEstimationEnabled && finalStatementInfo ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.smSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, "Final statement"), status === 'ProcessingError' ? getCriticalChip('There is an issue with your statement. Please contact the support') : /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, getText())), /*#__PURE__*/React.createElement("div", {
    className: "".concat(disabled || isInactive ? moveOutClasses.disabled : '', " ").concat(moveOutClasses.fseBlock)
  }, /*#__PURE__*/React.createElement("div", {
    className: moveOutClasses.fseBlockLeftPart
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "paper-dollar"
  }), /*#__PURE__*/React.createElement("div", {
    className: moveOutClasses.fseBlockText
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, residentName || 'N/A', " final statement"), /*#__PURE__*/React.createElement("div", {
    className: classes.xssSection
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "light",
    variant: "h6"
  }, "Unit ", unitName, ", move-out on ", dateToFormat(finalStatementInfo.moveOutDate))), isInactive ? null : /*#__PURE__*/React.createElement(Typography, {
    color: "light",
    variant: "h6"
  }, "Final statement charges $", finalStatementInfo.totalCharge))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Chip, {
    className: moveOutClasses.fseBlockChip,
    label: getChipProps().text,
    size: "small",
    variant: getChipProps().variant
  }), /*#__PURE__*/React.createElement("div", {
    className: moveOutClasses.fseBlockButton
  }, status === 'SentToResident' ? /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    component: RouterLink,
    size: "medium",
    target: "_blank",
    to: "/resident_acount?id=".concat(residencyId, "&accountId=").concat(accountId, "&propertyId=").concat(propertyId, "&tab=statements"),
    variant: "actionIcon",
    'custom-attribute': '250-Move-out-tab'
  }, /*#__PURE__*/React.createElement(Icon, {
    fontSize: "small",
    type: "show-eye-on"
  }), /*#__PURE__*/React.createElement("span", {
    className: moveOutClasses.fseBlockButtonText
  }, "Preview")) : isInactive ? null : /*#__PURE__*/React.createElement(FSEBillModal, {
    disabled: store.fse.submitFinalStatementsAsync.inProgress,
    isOpen: store.fse.FSEBillModalIsOpen,
    propertyId: propertyId,
    residentName: residentName,
    residencyId: residencyId,
    setFSEBillModalIsOpen: store.fse.setFSEBillModalIsOpen,
    submitFSE: handleSubmitFinalStatements,
    unitName: unitName
  }))))) : null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.footer, " ").concat(moveOutClasses.footer)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.submitButton
  }, !store.company.companyFeatureStore.readOnlyScheduleMoveOut && /*#__PURE__*/React.createElement(Button, {
    onClick: () => handleSubmit(),
    variant: "contained",
    'custom-attribute': '251-Move-out-tab'
  }, t('buttons.submit')), finalStatementInfo && !store.company.companyFeatureStore.isAppfolio ? /*#__PURE__*/React.createElement(Button, {
    disabled: disabled,
    onClick: handleCancel,
    style: {
      marginLeft: '5px'
    },
    variant: "contained",
    'custom-attribute': '252-Move-out-tab'
  }, t('unit_modal.cancel_scheduled_move-out')) : null)), /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isBatched === 'start' || isSubmitBillBatched === 'start' || store.activity.getBatchAsync.inProgress || store.fse.getFSEInfosAsync.inProgress || store.residencies.getResidencyBillingSettingsAsync.inProgress || store.residencies.getResidencyMoveOutDateAsync.inProgress || store.residencies.updateResidencyMoveOutDateAsync.inProgress
  })));
});